'use client';

import { useEffect } from 'react';
import { getStoredRedirectPath, validateState } from '@/lib/keycloak/sandbox-implicit-auth';

/**
 * Hook to handle deep link authentication callbacks from Electron.
 * Listens for deep-link events and processes auth tokens.
 */
export function useDeepLinkAuth() {
  useEffect(() => {
    const electronAPI = (window as any).electronAPI;
    if (typeof window === 'undefined' || !electronAPI?.deepLink) {
      return; // Not in Electron, skip
    }
    const handleDeepLink = async (url: string) => {
      if (!url.includes('://auth/callback')) {
        return; // Not an auth callback, ignore
      }
      try {
        const urlObj = new URL(url);
        const fragment = urlObj.hash.substring(1); // Remove '#'

        const params = new URLSearchParams(fragment);
        const accessToken = params.get('access_token');
        const idToken = params.get('id_token');
        const state = params.get('state');
        const error = params.get('error');
        if (error) {
          console.error('[auth] Auth error:', error);
          window.location.href = `/auth/signin?error=${encodeURIComponent(error)}`;
          return;
        }
        if (!state) {
          console.error('[auth] Missing state parameter');
          window.location.href = '/auth/signin?error=missing_state';
          return;
        }

        // Validate state
        if (!validateState(state)) {
          console.error('[auth] Invalid state parameter');
          window.location.href = '/auth/signin?error=invalid_state';
          return;
        }
        if (!accessToken || !idToken) {
          console.error('[auth] Missing tokens');
          window.location.href = '/auth/signin?error=missing_tokens';
          return;
        }

        // Store tokens
        const redirectPath = getStoredRedirectPath();
        try {
          localStorage.setItem('keycloak_access_token', accessToken);
          localStorage.setItem('keycloak_id_token', idToken);
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = '/api/auth/set-token';
          const accessTokenInput = document.createElement('input');
          accessTokenInput.type = 'hidden';
          accessTokenInput.name = 'accessToken';
          accessTokenInput.value = accessToken;
          form.appendChild(accessTokenInput);
          const idTokenInput = document.createElement('input');
          idTokenInput.type = 'hidden';
          idTokenInput.name = 'idToken';
          idTokenInput.value = idToken;
          form.appendChild(idTokenInput);
          const redirectInput = document.createElement('input');
          redirectInput.type = 'hidden';
          redirectInput.name = 'redirect';
          redirectInput.value = redirectPath;
          form.appendChild(redirectInput);
          document.body.appendChild(form);
          form.submit();
        } catch (err_0) {
          console.error('[auth] Error storing tokens:', err_0);
          window.location.href = '/auth/signin?error=token_storage_failed';
        }
      } catch (err) {
        console.error('[auth] Error handling deep link:', err);
        window.location.href = '/auth/signin?error=callback_failed';
      }
    };
    const unsubscribe = electronAPI.deepLink.onDeepLink(handleDeepLink);
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);
}